import React, { useEffect, useState, useCallback } from 'react';
import { useApp, api, MEDIA } from '@/context/AppContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, ArrowLeft, Trash2, Target, Trophy, Award, TrendingUp, Search,
  CheckCircle2, Clock, Sparkles, FileText, Printer, Download, Loader2
} from 'lucide-react';

// ============================================================
// 1) LIST OF IEPs (entry page)
// ============================================================
export function StudentPlansListPage() {
  const { t, lang, user } = useApp();
  const [ieps, setIeps] = useState([]);
  const [students, setStudents] = useState([]);
  const [openNew, setOpenNew] = useState(false);

  const reload = useCallback(() => {
    api.get('/iep').then(r => setIeps(r.data));
    api.get('/students').then(r => setStudents(r.data));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="student-plans-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">{lang === 'ar' ? 'الخطط الفردية للطلاب (IEP)' : 'Student IEPs'}</h1>
          <p className="text-sm text-muted-foreground mt-1">{lang === 'ar' ? 'ربط أهداف المنهج بالطلاب، قياس التقدم، وإصدار شهادات تلقائية عند الإتقان.' : 'Link curriculum goals to students, measure progress, and auto-issue certificates on mastery.'}</p>
        </div>
        <Button onClick={() => setOpenNew(true)} className="bg-primary hover:bg-primary/90 h-11" data-testid="new-iep-btn">
          <Plus className="w-4 h-4 me-1" />{lang === 'ar' ? 'خطة فردية جديدة' : 'New IEP'}
        </Button>
      </div>

      {ieps.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Target className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">{lang === 'ar' ? 'لا توجد خطط بعد. ابدأ بإضافة طالب أولاً ثم أنشئ خطة فردية له.' : 'No plans yet. Add a student first then create an IEP.'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ieps.map(p => <IEPCard key={p.id} iep={p} />)}
        </div>
      )}

      <NewIEPDialog open={openNew} onClose={() => setOpenNew(false)} students={students} onCreated={() => { setOpenNew(false); reload(); }} />
    </main>
  );
}

function IEPCard({ iep }) {
  const { lang } = useApp();
  const overall = iep.overall_progress || 0;
  const total = (iep.goals || []).length;
  const mastered = iep.mastered_count || 0;
  return (
    <Card className="card-elevate" data-testid={`iep-card-${iep.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-bold text-base">{iep.student_name_ar}</div>
            <div className="text-xs text-muted-foreground">{iep.disability_type} · {iep.age_group}</div>
          </div>
          <Badge variant="secondary" className="text-xs shrink-0">{total} {lang === 'ar' ? 'هدف' : 'goals'}</Badge>
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-muted-foreground">{lang === 'ar' ? 'التقدم الكلي' : 'Overall progress'}</span>
            <span className="font-bold text-primary stat-number">{overall}%</span>
          </div>
          <Progress value={overall} className="h-2" />
        </div>

        <div className="flex items-center gap-2 mb-3 text-xs">
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">
            <Trophy className="w-3 h-3 me-1" />{mastered} {lang === 'ar' ? 'متقن' : 'mastered'}
          </Badge>
          <Badge variant="outline" className="text-xs">{total - mastered} {lang === 'ar' ? 'قيد العمل' : 'in progress'}</Badge>
        </div>

        <Button asChild size="sm" variant="outline" className="w-full" data-testid={`open-iep-${iep.id}`}>
          <Link to={`/student-plans/${iep.id}`}>{lang === 'ar' ? 'فتح الخطة' : 'Open plan'}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function NewIEPDialog({ open, onClose, students, onCreated }) {
  const { lang } = useApp();
  const [form, setForm] = useState({ student_id: '', mastery_threshold: 80 });
  const submit = async () => {
    const student = students.find(s => s.id === form.student_id);
    if (!student) { toast.error(lang === 'ar' ? 'اختر طالباً' : 'Pick a student'); return; }
    try {
      await api.post('/iep', {
        student_id: student.id,
        student_name_ar: student.full_name_ar,
        student_name_en: student.full_name_en || '',
        disability_type: student.disability_type,
        disability_level: student.disability_level,
        age_group: student.age_group,
        center_name: student.center || '',
        goals: [],
        mastery_threshold: Number(form.mastery_threshold) || 80,
      });
      toast.success(lang === 'ar' ? 'تم إنشاء الخطة' : 'IEP created');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'error'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="new-iep-dialog">
        <DialogHeader><DialogTitle>{lang === 'ar' ? 'خطة فردية جديدة' : 'New IEP'}</DialogTitle><DialogDescription className="sr-only">Create IEP</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{lang === 'ar' ? 'الطالب' : 'Student'}</Label>
            <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
              <SelectTrigger data-testid="new-iep-student"><SelectValue placeholder={lang === 'ar' ? '— اختر —' : '— pick —'} /></SelectTrigger>
              <SelectContent>
                {students.length === 0 && <SelectItem value="__none" disabled>{lang === 'ar' ? 'لا يوجد طلاب. أضف طالباً من لوحة المعلم.' : 'No students. Add one in Teacher dashboard.'}</SelectItem>}
                {students.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name_ar} · {s.disability_type}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{lang === 'ar' ? 'حد الإتقان (٪)' : 'Mastery threshold (%)'}</Label>
            <Input type="number" min="50" max="100" value={form.mastery_threshold} onChange={(e) => setForm({ ...form, mastery_threshold: Number(e.target.value) })} data-testid="new-iep-threshold" />
            <p className="text-[10px] text-muted-foreground mt-1">{lang === 'ar' ? 'تُصدر شهادة آلية عند تجاوز أعلى تقييم لهذا الحد.' : 'A certificate is auto-issued when the best score crosses this threshold.'}</p>
          </div>
          <Button onClick={submit} disabled={!form.student_id} className="w-full bg-primary" data-testid="create-iep-btn">{lang === 'ar' ? 'إنشاء' : 'Create'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// 2) IEP DETAIL — goals + progress + certificates
// ============================================================
export function StudentPlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang, user } = useApp();
  const [iep, setIep] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [recordFor, setRecordFor] = useState(null); // goal object
  const [certOpen, setCertOpen] = useState(null);

  const canManage = user && ['teacher', 'admin'].includes(user.role);

  const reload = useCallback(() => {
    api.get(`/iep/${id}/full`).then(r => setIep(r.data)).catch(() => setIep(null));
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  // LAZY BATCH TRANSLATION: when the page is in English but any attached goal
  // is missing its English text, fire translate-goal in parallel. This is the
  // same idempotent endpoint used in the Curriculum Library.
  useEffect(() => {
    if (!iep || lang !== 'en') return;
    const goals = iep.goals_full || [];
    const missing = goals.filter(g => g && g.main_goal_ar && !g.main_goal_en);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        await Promise.all(missing.slice(0, 12).map(g =>
          api.post(`/curriculum/translate-goal/${g.id}`).catch(() => null)
        ));
        if (!cancelled) reload();
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [lang, iep?.id, iep?.goals_full?.length, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!iep) return <main id="main" className="p-8 text-center text-muted-foreground" data-testid="iep-loading">{lang === 'ar' ? 'جارٍ التحميل...' : 'Loading...'}</main>;

  const goalsFull = iep.goals_full || [];
  const overall = iep.overall_progress || 0;
  const mastered = iep.mastered_count || 0;

  const removeGoal = async (goalId) => {
    await api.delete(`/iep/${id}/remove-goal/${goalId}`);
    toast.success(lang === 'ar' ? 'تمت الإزالة' : 'Removed');
    reload();
  };

  const addGoal = async (goalId) => {
    await api.post(`/iep/${id}/add-goal/${goalId}`);
    toast.success(lang === 'ar' ? 'تمت الإضافة' : 'Added');
    reload();
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-6" data-testid="iep-detail-page">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(canManage ? '/student-plans' : (user?.role === 'parent' ? '/parent' : '/student'))} data-testid="back-to-plans">
          <ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{canManage ? (lang === 'ar' ? 'الخطط' : 'Plans') : (lang === 'ar' ? 'الرئيسية' : 'Home')}
        </Button>
      </div>

      {/* Header card */}
      <Card className="mb-6 overflow-hidden">
        <CardContent className="p-6 bg-gradient-to-br from-primary/5 via-accent/5 to-transparent">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-primary" />
                <span className="text-xs font-bold text-primary uppercase tracking-wide">{lang === 'ar' ? 'الخطة الفردية' : 'IEP'}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black mb-2">{iep.student_name_ar}</h1>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Badge variant="secondary">{iep.disability_type}</Badge>
                <Badge variant="outline">{iep.disability_level}</Badge>
                <Badge variant="outline">{iep.age_group}</Badge>
                {iep.center_name && <Badge variant="outline">{iep.center_name}</Badge>}
                <Badge className="bg-accent/15 text-accent-foreground border-accent/30">{lang === 'ar' ? 'حد الإتقان' : 'Mastery'}: {iep.mastery_threshold}%</Badge>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold">{lang === 'ar' ? 'التقدم الإجمالي' : 'Overall progress'}</span>
                  <span className="text-2xl font-black text-primary stat-number">{overall}%</span>
                </div>
                <Progress value={overall} className="h-3" />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat icon={Target} value={goalsFull.length} label={lang === 'ar' ? 'أهداف' : 'goals'} />
                <Stat icon={Trophy} value={mastered} label={lang === 'ar' ? 'متقن' : 'mastered'} color="text-emerald-600" />
                <Stat icon={Clock} value={goalsFull.length - mastered} label={lang === 'ar' ? 'قيد العمل' : 'in progress'} color="text-amber-600" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action bar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-bold text-lg">{lang === 'ar' ? 'أهداف الخطة' : 'Plan goals'}</h2>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/iep/${id}/export-html?language=${lang}`;
              const token = localStorage.getItem('token');
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.text())
                .then(html => { const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); } });
            }}
            data-testid="export-iep-pdf"
          >
            <FileText className="w-4 h-4 me-1" />{lang === 'ar' ? 'تصدير PDF' : 'Export PDF'}
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="view-certificates">
            <Link to={`/student-plans/${id}/certificates`}><Award className="w-4 h-4 me-1" />{lang === 'ar' ? 'الشهادات' : 'Certificates'}</Link>
          </Button>
          {canManage && (
            <Button onClick={() => setPickerOpen(true)} className="bg-primary" data-testid="add-goal-btn">
              <Plus className="w-4 h-4 me-1" />{lang === 'ar' ? 'إضافة هدف' : 'Add goal'}
            </Button>
          )}
        </div>
      </div>

      {goalsFull.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Target className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm mb-3">{lang === 'ar' ? 'لم تتم إضافة أهداف بعد. اختر من ٣٬٠٩٦ هدفاً في المنهج.' : 'No goals yet. Pick from 3,096 curriculum goals.'}</p>
          {canManage && <Button onClick={() => setPickerOpen(true)} className="bg-primary" data-testid="add-goal-empty">{lang === 'ar' ? 'إضافة أول هدف' : 'Add first goal'}</Button>}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {goalsFull.map(g => (
            <IEPGoalRow key={g.id} goal={g} threshold={iep.mastery_threshold} canManage={canManage} onRecord={() => setRecordFor(g)} onRemove={() => removeGoal(g.id)} onCert={(cid) => setCertOpen(cid)} />
          ))}
        </div>
      )}

      {pickerOpen && <GoalPickerForIEP existingIds={iep.goals || []} studentProfile={{ disability_type: iep.disability_type, age_group: iep.age_group, disability_level: iep.disability_level }} onClose={() => setPickerOpen(false)} onPick={async (g) => { await addGoal(g.id); setPickerOpen(false); }} />}
      {recordFor && <RecordProgressDialog iepId={id} goal={recordFor} onClose={() => setRecordFor(null)} onRecorded={() => { setRecordFor(null); reload(); }} />}
      {certOpen && <CertificateViewDialog certId={certOpen} onClose={() => setCertOpen(null)} />}
    </main>
  );
}

function Stat({ icon: Icon, value, label, color = 'text-primary' }) {
  return (
    <div className="border border-border rounded-lg p-2 bg-white">
      <Icon className={`w-4 h-4 ${color} mx-auto mb-1`} />
      <div className={`text-xl font-black ${color} stat-number`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function IEPGoalRow({ goal, threshold, canManage, onRecord, onRemove, onCert }) {
  const { lang } = useApp();
  const p = goal.progress || {};
  const best = p.best || 0;
  const attempts = p.attempts || 0;
  const mastered = p.mastered;
  const percent = best;
  const colorBg = mastered ? 'bg-emerald-50 border-emerald-300' : best >= threshold * 0.6 ? 'bg-amber-50 border-amber-200' : 'bg-white';
  const tv = (ar, en) => lang === 'ar' ? ar : (en || ar);
  const translationPending = lang === 'en' && goal.main_goal_ar && !goal.main_goal_en;

  return (
    <div className={`border-2 rounded-xl p-4 ${colorBg} card-elevate`} data-testid={`iep-goal-${goal.goal_code}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="secondary" className="font-mono text-[10px]">{goal.goal_code}</Badge>
            <Badge className="bg-primary/10 text-primary text-[10px]" variant="outline">{tv(goal.sport_activity_ar, goal.sport_activity_en)}</Badge>
            <Badge variant="outline" className="text-[10px]">{tv(goal.performance_level_ar, goal.performance_level_en)}</Badge>
            {mastered && (
              <Badge className="bg-emerald-600 text-white text-[10px]">
                <CheckCircle2 className="w-3 h-3 me-1" />{lang === 'ar' ? 'تم الإتقان' : 'Mastered'}
              </Badge>
            )}
            {translationPending && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[9px]" data-testid={`tr-pending-${goal.goal_code}`}>
                <Loader2 className="w-2.5 h-2.5 me-0.5 animate-spin" />
                Translating…
              </Badge>
            )}
          </div>
          <div className="font-bold text-sm mb-1 line-clamp-2">{tv(goal.main_goal_ar, goal.main_goal_en)}</div>
          {goal.sub_goal_ar && <div className="text-xs text-muted-foreground line-clamp-1">{tv(goal.sub_goal_ar, goal.sub_goal_en)}</div>}

          <div className="mt-3 grid sm:grid-cols-4 gap-2 text-xs">
            <MiniStat label={lang === 'ar' ? 'أعلى' : 'Best'} value={`${p.best || 0}%`} highlight={mastered} />
            <MiniStat label={lang === 'ar' ? 'الأخير' : 'Latest'} value={`${p.latest || 0}%`} />
            <MiniStat label={lang === 'ar' ? 'المتوسط' : 'Avg'} value={`${p.average || 0}%`} />
            <MiniStat label={lang === 'ar' ? 'المحاولات' : 'Attempts'} value={attempts} />
          </div>

          {/* Pre / Formative / Post / Gain — professional assessment triad */}
          {(p.pre_score !== null && p.pre_score !== undefined) || (p.formative_avg !== null && p.formative_avg !== undefined) || (p.post_score !== null && p.post_score !== undefined) ? (
            <div className="mt-2 grid grid-cols-4 gap-2 text-xs" data-testid={`iep-triad-${goal.goal_code}`}>
              <MiniStat
                label={lang === 'ar' ? 'قبلي' : 'Pre'}
                value={p.pre_score !== null && p.pre_score !== undefined ? `${p.pre_score}%` : '—'}
              />
              <MiniStat
                label={lang === 'ar' ? 'تكويني' : 'Formative'}
                value={p.formative_avg !== null && p.formative_avg !== undefined ? `${p.formative_avg}% (${p.formative_count || 0})` : '—'}
              />
              <MiniStat
                label={lang === 'ar' ? 'بعدي' : 'Post'}
                value={p.post_score !== null && p.post_score !== undefined ? `${p.post_score}%` : '—'}
              />
              <MiniStat
                label={lang === 'ar' ? 'الفارق' : 'Gain'}
                value={p.learning_gain !== null && p.learning_gain !== undefined ? `${p.learning_gain > 0 ? '+' : ''}${p.learning_gain}%` : '—'}
                highlight={typeof p.learning_gain === 'number' && p.learning_gain > 0}
              />
            </div>
          ) : null}

          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-muted-foreground">{lang === 'ar' ? 'نسبة التقدم (أفضل تقييم)' : 'Progress (best score)'}</span>
              <span className="font-bold">{percent}% / {threshold}%</span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          {canManage && (
            <Button onClick={onRecord} size="sm" className="bg-primary hover:bg-primary/90" data-testid={`record-${goal.goal_code}`}>
              <TrendingUp className="w-4 h-4 me-1" />{lang === 'ar' ? 'تسجيل تقييم' : 'Record score'}
            </Button>
          )}
          {mastered && p.certificate_id && (
            <Button onClick={() => onCert(p.certificate_id)} size="sm" variant="outline" className="border-emerald-300 text-emerald-700" data-testid={`view-cert-${goal.goal_code}`}>
              <Award className="w-4 h-4 me-1" />{lang === 'ar' ? 'الشهادة' : 'Certificate'}
            </Button>
          )}
          {canManage && (
            <Button onClick={onRemove} size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive" aria-label="remove" data-testid={`remove-${goal.goal_code}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <div className={`rounded-md px-2 py-1.5 ${highlight ? 'bg-emerald-100 border border-emerald-300' : 'bg-secondary/60'}`}>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={`font-bold text-sm stat-number ${highlight ? 'text-emerald-700' : 'text-foreground'}`}>{value}</div>
    </div>
  );
}

// ----- Goal Picker (search the full 3,096-goal library, smart-filtered by student profile) -----
function GoalPickerForIEP({ existingIds, studentProfile, onPick, onClose }) {
  const { lang } = useApp();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // Smart filters initialized from the student's profile so picker only shows
  // age- and disability-appropriate goals by default. Teacher can relax each one.
  const [fDisability, setFDisability] = useState(studentProfile?.disability_type || '');
  const [fAge, setFAge] = useState(studentProfile?.age_group || '');
  const [fLevel, setFLevel] = useState(studentProfile?.disability_level || '');

  // Fetch ALL filtered goals in one request (backend cap is 3200 covering the entire curriculum).
  // This matches the user requirement: the IEP picker must always show every goal that
  // matches the student's disability + age + level — not a paginated slice.
  const FETCH_LIMIT = 3200;

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { q: q || undefined, limit: FETCH_LIMIT, skip: 0 };
      if (fDisability) params.disability_type = fDisability;
      if (fAge) params.age_group = fAge;
      if (fLevel) params.disability_level = fLevel;
      api.get('/curriculum/goals', { params }).then(r => { setItems(r.data.items); setTotal(r.data.total); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, fDisability, fAge, fLevel]);

  const matchScore = items.length;
  const clearFilters = () => { setFDisability(''); setFAge(''); setFLevel(''); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="iep-goal-picker-dialog">
        <DialogHeader>
          <DialogTitle>{lang === 'ar' ? 'اختر هدفاً من المنهج' : 'Pick a goal from the curriculum'}</DialogTitle>
          <DialogDescription>{lang === 'ar' ? `مفلتر تلقائياً بحسب ملف الطالب — ${matchScore} نتيجة من ${total}` : `Auto-filtered by student profile — ${matchScore} of ${total} match`}</DialogDescription>
        </DialogHeader>

        {/* Smart filter chips */}
        <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="iep-picker-filters">
          {fDisability && (
            <FilterChip onClear={() => setFDisability('')} label={`👤 ${fDisability}`} testid="chip-disability" />
          )}
          {fAge && (
            <FilterChip onClear={() => setFAge('')} label={`🎂 ${fAge}`} testid="chip-age" />
          )}
          {fLevel && (
            <FilterChip onClear={() => setFLevel('')} label={`📊 ${fLevel}`} testid="chip-level" />
          )}
          {(fDisability || fAge || fLevel) && (
            <button onClick={clearFilters} className="text-[10px] text-muted-foreground underline hover:text-foreground ms-1" data-testid="clear-filters">
              {lang === 'ar' ? 'إزالة كل الفلاتر' : 'Clear all'}
            </button>
          )}
          {!fDisability && !fAge && !fLevel && (
            <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              {lang === 'ar' ? '⚠ فلاتر الطالب مُلغاة — تعرض كل الأهداف' : '⚠ Profile filters off — showing all goals'}
            </span>
          )}
        </div>

        <div className="relative mb-3">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
          <Input placeholder={lang === 'ar' ? 'ابحث... (الكود، الرياضة، الهدف)' : 'Search... (code, sport, goal)'} value={q} onChange={(e) => setQ(e.target.value)} className="ps-10" data-testid="iep-picker-search" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-2">
          {items.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-8" data-testid="picker-empty">
              <p>{lang === 'ar' ? 'لا توجد أهداف تطابق ملف الطالب.' : 'No goals match the student profile.'}</p>
              {(fDisability || fAge || fLevel) && (
                <button onClick={clearFilters} className="text-primary underline mt-2 text-xs">
                  {lang === 'ar' ? 'جرّب إزالة الفلاتر' : 'Try removing filters'}
                </button>
              )}
            </div>
          ) : items.map(g => {
            const already = existingIds.includes(g.id);
            return (
              <button
                key={g.id}
                disabled={already}
                onClick={() => onPick(g)}
                data-testid={`iep-pick-${g.goal_code}`}
                className={`w-full text-start border rounded-lg p-3 transition-colors ${already ? 'bg-secondary/60 cursor-not-allowed opacity-60' : 'border-border hover:border-primary hover:bg-primary/5'}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant="secondary" className="font-mono text-[10px]">{g.goal_code}</Badge>
                  <Badge className="bg-primary/10 text-primary text-[10px]" variant="outline">{lang === 'ar' ? g.performance_level_ar : (g.performance_level_en || g.performance_level_ar)}</Badge>
                  {already && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" variant="outline">✓ {lang === 'ar' ? 'مضاف' : 'Added'}</Badge>}
                </div>
                <div className="font-semibold text-sm line-clamp-2 mb-1">{lang === 'ar' ? g.main_goal_ar : (g.main_goal_en || g.main_goal_ar)}</div>
                <div className="text-[10px] text-muted-foreground">
                  {lang === 'ar' ? g.disability_type_ar : (g.disability_type_en || g.disability_type_ar)} · {lang === 'ar' ? g.age_group_ar : (g.age_group_en || g.age_group_ar)} · {lang === 'ar' ? g.sport_activity_ar : (g.sport_activity_en || g.sport_activity_ar)}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FilterChip({ label, onClear, testid }) {
  return (
    <span className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/30 rounded-full px-2 py-0.5 text-[11px] font-bold" data-testid={testid}>
      {label}
      <button onClick={onClear} className="ms-1 opacity-70 hover:opacity-100" aria-label="clear">×</button>
    </span>
  );
}

// ----- Record progress dialog -----
function RecordProgressDialog({ iepId, goal, onClose, onRecorded }) {
  const { lang } = useApp();
  const [score, setScore] = useState(50);
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState('formative');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/iep/${iepId}/record-progress`, {
        goal_id: goal.id,
        score: Number(score),
        notes,
        phase,
      });
      if (data.new_certificate_id) {
        toast.success(lang === 'ar' ? '🎉 تهانينا! تم الإتقان وإصدار شهادة!' : '🎉 Mastered! Certificate issued.');
      } else {
        toast.success(lang === 'ar' ? `تم التسجيل (التقدم: ${data.goal_progress.best}%)` : `Recorded (best: ${data.goal_progress.best}%)`);
      }
      onRecorded();
    } catch (e) { toast.error(e.response?.data?.detail || 'error'); }
    finally { setBusy(false); }
  };

  const PHASES = [
    { v: 'pre',       ar: 'قبلي',   en: 'Pre-test',   hint_ar: 'قبل بدء التعليم', hint_en: 'Before teaching',   color: 'bg-sky-100 text-sky-800 border-sky-300' },
    { v: 'formative', ar: 'تكويني', en: 'Formative',  hint_ar: 'أثناء التعليم',     hint_en: 'During teaching',   color: 'bg-amber-100 text-amber-800 border-amber-300' },
    { v: 'post',      ar: 'بعدي',   en: 'Post-test',  hint_ar: 'بعد إتمام التعليم', hint_en: 'After teaching',    color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="record-progress-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />{lang === 'ar' ? 'تسجيل تقييم' : 'Record assessment score'}</DialogTitle>
          <DialogDescription className="sr-only">Record score for goal</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-primary/5 rounded-lg p-3">
            <Badge variant="secondary" className="font-mono text-[10px] mb-1">{goal.goal_code}</Badge>
            <div className="font-bold text-sm">{lang === 'ar' ? goal.main_goal_ar : (goal.main_goal_en || goal.main_goal_ar)}</div>
          </div>

          <div>
            <Label className="text-xs mb-1 block">{lang === 'ar' ? 'نوع الاختبار' : 'Assessment phase'}</Label>
            <div className="grid grid-cols-3 gap-1.5" data-testid="phase-selector">
              {PHASES.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setPhase(p.v)}
                  data-testid={`phase-${p.v}`}
                  className={`px-2 py-2 rounded-lg text-xs font-bold border-2 transition-all ${phase === p.v ? p.color + ' shadow-md scale-[1.02]' : 'bg-stone-50 border-stone-200 text-stone-600 hover:bg-stone-100'}`}
                >
                  <div>{lang === 'ar' ? p.ar : p.en}</div>
                  <div className="text-[9px] font-normal opacity-70 mt-0.5">{lang === 'ar' ? p.hint_ar : p.hint_en}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs flex items-center justify-between">
              <span>{lang === 'ar' ? 'الدرجة (٠–١٠٠)' : 'Score (0-100)'}</span>
              <span className="font-bold text-primary text-base stat-number">{score}%</span>
            </Label>
            <input
              type="range" min="0" max="100" step="5"
              value={score}
              onChange={(e) => setScore(Number(e.target.value))}
              className="w-full accent-primary"
              data-testid="score-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>0</span><span>50</span><span>100</span>
            </div>
          </div>

          <div>
            <Label className="text-xs">{lang === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="score-notes" />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={submit} disabled={busy} className="flex-1 bg-primary" data-testid="submit-progress">
              {busy ? '...' : (lang === 'ar' ? 'حفظ التقييم' : 'Save score')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ----- Certificate viewer -----
// Beautifully redesigned: golden double frame, ornate corner flourishes (SVG),
// laurel wreaths, ribbon with serial, official watermark seal, signature lines.
function CertificateViewDialog({ certId, onClose }) {
  const { lang } = useApp();
  const [cert, setCert] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => { api.get(`/certificates/${certId}`).then(r => setCert(r.data)).catch(() => setCert(null)); }, [certId]);

  // LAZY TRANSLATION — when viewing in English and the cert has no achievement_en
  // or student_name_en, ask the backend to fill them in (idempotent, persists).
  useEffect(() => {
    if (!cert || lang !== 'en') return;
    if (cert.achievement_en && cert.student_name_en) return;
    let cancelled = false;
    setTranslating(true);
    api.post(`/certificates/${certId}/translate`)
      .then(r => { if (!cancelled) setCert(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [cert?.id, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAr = lang === 'ar';
  // Language-aware fields with safe fallback
  const displayName = cert ? (isAr ? cert.student_name : (cert.student_name_en || cert.student_name)) : '';
  const displayAchievement = cert ? (isAr ? cert.achievement_ar : (cert.achievement_en || cert.achievement_ar)) : '';
  const formattedDate = cert ? new Date(cert.date).toLocaleDateString(isAr ? 'ar-AE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const shortSerial = cert ? (cert.id || '').replace(/-/g, '').slice(0, 10).toUpperCase() : '';

  const printIt = () => {
    if (!cert) return;
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(`<!doctype html><html lang="${lang}" dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>Certificate ${cert.goal_code || ''}</title>
      <style>
        @page { size: A4 landscape; margin: 0; }
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Cairo', 'Georgia', serif; background: #FAF7EE; }
        .page { width: 297mm; height: 210mm; padding: 14mm; position: relative; }
        .outer { position: absolute; inset: 8mm; border: 3px solid #B45309; border-radius: 6mm; }
        .inner { position: absolute; inset: 12mm; border: 1px solid #B45309; border-radius: 5mm; padding: 14mm; background:
          radial-gradient(ellipse at top, rgba(180,83,9,0.05), transparent 60%),
          radial-gradient(ellipse at bottom, rgba(20,83,45,0.05), transparent 60%),
          #FFFDF7; }
        .corner { position: absolute; width: 64px; height: 64px; color: #B45309; }
        .corner.tl { top: 8mm; left: 8mm; transform: rotate(0deg); }
        .corner.tr { top: 8mm; right: 8mm; transform: rotate(90deg); }
        .corner.bl { bottom: 8mm; left: 8mm; transform: rotate(-90deg); }
        .corner.br { bottom: 8mm; right: 8mm; transform: rotate(180deg); }
        .seal { position: absolute; ${isAr ? 'left' : 'right'}: 26mm; bottom: 28mm; width: 95px; height: 95px; border: 3px double #14532D; border-radius: 50%; display: grid; place-items: center; transform: rotate(-12deg); color: #14532D; font-weight: 900; font-size: 9px; text-align: center; line-height: 1.2; background: rgba(255,253,247,0.95); }
        .seal::before { content: ''; position: absolute; inset: 6px; border: 1px dashed #14532D; border-radius: 50%; }
        .watermark { position: absolute; inset: 0; display: grid; place-items: center; opacity: 0.04; font-size: 200px; font-weight: 900; color: #14532D; pointer-events: none; user-select: none; }
        h1 { color: #14532D; font-size: 44px; margin: 6px 0 4px 0; letter-spacing: 3px; font-weight: 900; }
        .sub-title { color: #B45309; font-size: 13px; letter-spacing: 6px; text-transform: uppercase; margin-bottom: 18px; }
        .award-line { color: #44403C; font-size: 16px; margin: 18px 0 6px 0; font-style: italic; }
        .name { font-size: 56px; font-weight: 900; color: #B45309; margin: 4px 0 18px 0; padding: 0 24px 10px 24px; display: inline-block; border-bottom: 2px solid #B45309; font-family: 'Cairo', 'Times New Roman', serif; }
        .ach-label { font-size: 14px; color: #44403C; margin-top: 14px; }
        .ach { font-size: 19px; font-weight: 700; color: #1C1917; max-width: 200mm; margin: 6px auto 12px auto; line-height: 1.5; }
        .stats-row { display: flex; justify-content: center; gap: 26px; margin-top: 14px; flex-wrap: wrap; }
        .stat { display: flex; flex-direction: column; align-items: center; gap: 4px; }
        .stat .v { font-size: 22px; font-weight: 900; color: #14532D; }
        .stat .l { font-size: 10px; color: #6B7280; text-transform: uppercase; letter-spacing: 2px; }
        .code-pill { display: inline-block; background: #14532D; color: white; padding: 4px 14px; border-radius: 999px; font-family: monospace; font-size: 11px; letter-spacing: 1.5px; }
        .ribbon { position: absolute; top: 14mm; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, #B45309 0%, #92400E 100%); color: white; padding: 8px 32px; font-weight: 800; letter-spacing: 2px; font-size: 11px; clip-path: polygon(0% 0%, 100% 0%, 92% 50%, 100% 100%, 0% 100%, 8% 50%); }
        .footer { position: absolute; left: 18mm; right: 18mm; bottom: 16mm; display: flex; justify-content: space-between; align-items: flex-end; font-size: 11px; color: #44403C; }
        .sig { text-align: center; min-width: 140px; }
        .sig .line { width: 100%; height: 1px; background: #14532D; margin-bottom: 6px; }
        .sig .role { font-weight: 700; }
        .center { text-align: center; }
        .small-meta { font-size: 10px; color: #6B7280; letter-spacing: 1px; margin-top: 4px; }
      </style></head><body>
      <div class='page'>
        <div class='outer'></div>
        <svg class='corner tl' viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'><path d='M2 2 H40 V6 H6 V40 H2 Z M10 10 H32 V12 H12 V32 H10 Z' fill='currentColor'/><circle cx='6' cy='6' r='3' fill='currentColor'/></svg>
        <svg class='corner tr' viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'><path d='M2 2 H40 V6 H6 V40 H2 Z M10 10 H32 V12 H12 V32 H10 Z' fill='currentColor'/><circle cx='6' cy='6' r='3' fill='currentColor'/></svg>
        <svg class='corner bl' viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'><path d='M2 2 H40 V6 H6 V40 H2 Z M10 10 H32 V12 H12 V32 H10 Z' fill='currentColor'/><circle cx='6' cy='6' r='3' fill='currentColor'/></svg>
        <svg class='corner br' viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'><path d='M2 2 H40 V6 H6 V40 H2 Z M10 10 H32 V12 H12 V32 H10 Z' fill='currentColor'/><circle cx='6' cy='6' r='3' fill='currentColor'/></svg>
        <div class='inner'>
          <div class='watermark'>★</div>
          <div class='ribbon'>${isAr ? 'وثيقة رسمية' : 'OFFICIAL DOCUMENT'}</div>
          <div class='center' style='padding-top: 36px;'>
            <div class='sub-title'>${isAr ? 'منصة همم للتربية الرياضية المعدّلة' : 'HIMAM PLATFORM · ADAPTED PE'}</div>
            <h1>${isAr ? 'شهادة إتقان' : 'CERTIFICATE OF MASTERY'}</h1>
            <div class='award-line'>${isAr ? 'تُمنح بفخر إلى' : 'This certificate is proudly awarded to'}</div>
            <div class='name'>${displayName}</div>
            <div class='ach-label'>${isAr ? 'تقديراً لإتقانه الهدف التعليمي' : 'In recognition of mastering the learning goal'}</div>
            <div class='ach'>«${displayAchievement}»</div>
            <div class='stats-row'>
              <div class='stat'><div class='v'>${cert.score}%</div><div class='l'>${isAr ? 'أعلى تقييم' : 'Best score'}</div></div>
              <div class='stat'><div class='v'><span class='code-pill'>${cert.goal_code || '—'}</span></div><div class='l'>${isAr ? 'رمز الهدف' : 'Goal code'}</div></div>
              <div class='stat'><div class='v' style='font-size: 16px;'>${formattedDate}</div><div class='l'>${isAr ? 'تاريخ الإصدار' : 'Date issued'}</div></div>
            </div>
          </div>
          <div class='seal'>${isAr ? 'مُعتمد<br>منصة<br>همم' : 'CERTIFIED<br>HIMAM<br>PLATFORM'}</div>
          <div class='footer'>
            <div>
              <div class='small-meta'>${isAr ? 'الرقم التسلسلي' : 'Serial #'}: ${shortSerial}</div>
              <div class='small-meta'>${isAr ? 'منهاج التربية البدنية المعدّلة' : 'Adapted PE Curriculum'}</div>
            </div>
            <div class='sig'>
              <div class='line'></div>
              <div class='role'>${isAr ? 'منسّق المنهج' : 'Curriculum Coordinator'}</div>
            </div>
          </div>
        </div>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),350)</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-transparent border-0 shadow-2xl" data-testid="certificate-dialog">
        <DialogHeader className="sr-only"><DialogTitle>{isAr ? 'شهادة إتقان' : 'Certificate of Mastery'}</DialogTitle><DialogDescription>Certificate</DialogDescription></DialogHeader>
        {!cert ? <div className="p-8 text-center text-muted-foreground bg-white rounded-xl">{isAr ? 'جارٍ التحميل...' : 'Loading...'}</div> : (
          <div className="bg-stone-100 p-3 rounded-2xl">
            {/* Certificate body — golden double frame with corner flourishes */}
            <div className="relative bg-[#FFFDF7] rounded-xl overflow-hidden" style={{ boxShadow: '0 20px 60px -20px rgba(180,83,9,0.4), inset 0 0 0 3px #B45309, inset 0 0 0 6px #FFFDF7, inset 0 0 0 7px #B45309' }}>
              {/* SVG corner flourishes */}
              <CornerFlourish className="top-4 start-4" />
              <CornerFlourish className="top-4 end-4 -scale-x-100" />
              <CornerFlourish className="bottom-4 start-4 -scale-y-100" />
              <CornerFlourish className="bottom-4 end-4 -scale-x-100 -scale-y-100" />

              {/* Watermark star */}
              <div className="absolute inset-0 grid place-items-center pointer-events-none select-none" aria-hidden="true">
                <div className="text-[12rem] text-emerald-900/[0.04] font-black">★</div>
              </div>

              {/* Ribbon banner */}
              <div className="relative pt-10 px-10 pb-2 text-center">
                <div className="absolute left-1/2 -translate-x-1/2 top-0 px-6 py-1.5 text-[10px] tracking-[0.25em] font-extrabold text-white"
                  style={{ background: 'linear-gradient(135deg, #B45309 0%, #92400E 100%)',
                           clipPath: 'polygon(0% 0%, 100% 0%, 92% 50%, 100% 100%, 0% 100%, 8% 50%)' }}>
                  {isAr ? 'وثيقة رسمية' : 'OFFICIAL DOCUMENT'}
                </div>

                <div className="text-[11px] tracking-[0.4em] text-amber-700 font-bold uppercase mt-4 mb-2">
                  {isAr ? 'منصة همم للتربية الرياضية المعدّلة' : 'Himam Platform · Adapted PE'}
                </div>

                <h1 className="text-3xl sm:text-4xl font-black text-emerald-900 tracking-[0.15em] mb-2" style={{ fontFamily: "'Cairo', 'Georgia', serif" }}>
                  {isAr ? 'شهادة إتقان' : 'CERTIFICATE OF MASTERY'}
                </h1>

                <div className="text-stone-600 italic text-sm mt-4">
                  {isAr ? 'تُمنح بفخر إلى' : 'This certificate is proudly awarded to'}
                </div>

                <div className="my-4 inline-block">
                  <div className="text-4xl sm:text-5xl font-black text-amber-700 px-6 pb-3 border-b-2 border-amber-700" style={{ fontFamily: "'Cairo', 'Times New Roman', serif" }}>
                    {displayName}
                    {translating && <span className="ms-2 inline-flex items-center text-[10px] font-normal text-amber-700/70 align-middle" data-testid="cert-translating"><Loader2 className="w-3 h-3 me-1 animate-spin" />translating…</span>}
                  </div>
                </div>

                <div className="text-stone-700 text-sm mt-3">
                  {isAr ? 'تقديراً لإتقانه الهدف التعليمي' : 'In recognition of mastering the learning goal'}
                </div>
                <div className="text-base sm:text-lg font-bold text-stone-900 mt-2 mb-4 max-w-xl mx-auto leading-snug">
                  «{displayAchievement}»
                </div>

                {/* Stats row */}
                <div className="flex items-center justify-center gap-6 sm:gap-10 mt-5 flex-wrap" data-testid="cert-stats">
                  <CertStat label={isAr ? 'أعلى تقييم' : 'Best score'} value={`${cert.score}%`} />
                  <CertStat label={isAr ? 'رمز الهدف' : 'Goal code'} value={
                    <span className="inline-block bg-emerald-900 text-white px-3 py-1 rounded-full font-mono text-xs tracking-widest">{cert.goal_code || '—'}</span>
                  } />
                  <CertStat label={isAr ? 'تاريخ الإصدار' : 'Date issued'} value={<span className="text-base font-bold text-emerald-900">{formattedDate}</span>} />
                </div>

                {/* Seal */}
                <div className="absolute bottom-6 end-8 sm:end-12 w-20 h-20 grid place-items-center text-emerald-900 -rotate-12 select-none" aria-hidden="true">
                  <div className="absolute inset-0 rounded-full border-[3px] border-double border-emerald-900 bg-amber-50/80" />
                  <div className="absolute inset-1.5 rounded-full border border-dashed border-emerald-900" />
                  <div className="relative font-black text-[8px] leading-tight text-center tracking-wider">
                    {isAr ? <>مُعتمد<br />منصة<br />همم</> : <>CERTIFIED<br />HIMAM<br />PLATFORM</>}
                  </div>
                </div>

                {/* Footer with signature */}
                <div className="flex items-end justify-between gap-6 mt-12 px-4 pb-4 pt-8 text-[11px] text-stone-600">
                  <div className="text-start max-w-[40%]">
                    <div className="font-mono tracking-widest"><span className="text-stone-400">{isAr ? 'الرقم التسلسلي' : 'Serial #'}:</span> {shortSerial}</div>
                    <div className="text-[10px] mt-0.5">{isAr ? 'منهاج التربية البدنية المعدّلة' : 'Adapted PE Curriculum'}</div>
                  </div>
                  <div className="text-center min-w-[160px] me-12 sm:me-16">
                    <div className="h-px bg-emerald-900 mb-1.5"></div>
                    <div className="font-bold text-emerald-900">{isAr ? 'منسّق المنهج' : 'Curriculum Coordinator'}</div>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={printIt} className="w-full bg-emerald-900 hover:bg-emerald-800 mt-3" data-testid="cert-print-btn">
              <Printer className="w-4 h-4 me-2" />{isAr ? 'طباعة / حفظ PDF' : 'Print / Save PDF'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CertStat({ label, value }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="text-xl font-black text-emerald-900">{value}</div>
      <div className="text-[9px] tracking-[0.2em] text-stone-500 uppercase font-bold">{label}</div>
    </div>
  );
}

function CornerFlourish({ className = '' }) {
  return (
    <svg viewBox="0 0 80 80" className={`absolute w-12 h-12 sm:w-16 sm:h-16 text-amber-700 ${className}`} aria-hidden="true">
      <path d="M4 4 Q4 24 12 24 L36 24" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M4 4 Q24 4 24 12 L24 36" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="4" cy="4" r="3" fill="currentColor" />
      <circle cx="24" cy="24" r="2" fill="currentColor" />
      <path d="M10 30 Q14 26 18 30 Q22 34 26 30" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M30 10 Q26 14 30 18 Q34 22 30 26" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

// ============================================================
// 3) ALL CERTIFICATES FOR AN IEP
// ============================================================
export function IEPCertificatesPage() {
  const { id } = useParams();
  const { lang, user } = useApp();
  const [certs, setCerts] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get('/certificates', { params: { iep_id: id } }).then(r => setCerts(r.data)); }, [id]);

  const canManage = user && ['teacher', 'admin'].includes(user.role);

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="iep-certs-page">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to={canManage ? `/student-plans/${id}` : (user?.role === 'parent' ? '/parent' : '/student')}><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'رجوع' : 'Back'}</Link>
      </Button>
      <h1 className="text-3xl font-black mb-6">🏆 {lang === 'ar' ? 'شهادات الإتقان' : 'Mastery Certificates'}</h1>
      {certs.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Award className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">{lang === 'ar' ? 'لم يصدر أي شهادة بعد. تُصدر تلقائياً عند بلوغ حد الإتقان لأي هدف.' : 'No certificates issued yet. They are auto-issued when a goal reaches the mastery threshold.'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {certs.map(c => (
            <button key={c.id} onClick={() => setOpen(c.id)} data-testid={`cert-card-${c.id}`} className="text-start card-elevate border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white rounded-xl p-5">
              <Award className="w-8 h-8 text-amber-600 mb-2" />
              <Badge variant="secondary" className="font-mono text-[10px] mb-2">{c.goal_code}</Badge>
              <div className="font-bold text-sm line-clamp-2 mb-1">{c.achievement_ar}</div>
              <div className="text-emerald-700 font-bold text-sm">{c.score}%</div>
              <div className="text-[10px] text-muted-foreground mt-1">{new Date(c.date).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-US')}</div>
            </button>
          ))}
        </div>
      )}
      {open && <CertificateViewDialog certId={open} onClose={() => setOpen(null)} />}
    </main>
  );
}
